'use client'

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { apiFetch } from '@/lib/api-fetch'
import type { MessageDispatch, YMarketOrder } from '@/lib/types'
import type { CdekTrackingData } from '@/lib/api/schemas/cdek.schema'
import type { MsgType } from '@/lib/order-storage'
import {
  saveSent, loadChatId, saveChatId,
  loadOrderFields, saveOrderField,
  SentEntry,
} from '@/lib/order-storage'
import {
  getCity, formatOrderCustomerName, orderShipment,
  EM_DASH,
} from '@/lib/order-helpers'
import { orderGreetingNameParts, resolveGreetingName } from '@/lib/name-heuristics'
import { formatRelativeTime, formatIsoDate, getDefaultShipDate } from '@/lib/utils'
import { useOrdersStore, statusKey, type BuyerInfo } from '@/lib/orders-store'
import {
  fetchOrders as apiFetchOrders, fetchOrderById, fetchBuyer,
  fetchMissionControl as apiFetchMissionControl, getOrderState, updateOrderState,
  markManualMessage, unmarkManualMessage,
} from '@/lib/api/client/orders'
import { fetchOrderChatHistory, sendChatMessage as apiSendChatMessage } from '@/lib/api/client/chats'
import type { OrderChatHistory } from '@/lib/ymarket-chat'
import { chatContainsSentMessage, historyConfirmsPendingChatDelivery } from '@/lib/chat-message-utils'
import { fetchCdekEstimate, fetchCdekTracking } from '@/lib/api/client/cdek'
import { renderHandoffTemplateWithText, renderPrenotifTemplate } from '@/lib/template'
import { composeCourierMessage } from '@/lib/courier-assignment'
import { getActiveCourierTemplateText, getActiveTemplateText, getActivePrenotifTemplateText } from '@/lib/template-storage'
import type { PendingChatDelivery } from '@/components/orders/YMarketChatPanel'
import type { ChatSendFlight, ChatSendOrigin } from '@/lib/chat-motion'
import { OrderDetailContent } from '@/components/orders/OrderDetailContent'
import { MissionControl } from '@/components/orders/MissionControl'
import { OrderDetailDrawer } from '@/components/orders/OrderDetailDrawer'
import { useOrderFieldsAutosave, type OrderFieldsDraft } from '@/components/orders/useOrderFieldsAutosave'
import { orderToTemplate } from '@/lib/order-helpers'
import { getCdekTransitPlan } from '@/lib/cdek-transit'
import { latestCdekStatus } from '@/lib/cdek-status-history'
import {
  DEFAULT_SHIPPING_SETTINGS,
  nextDispatchWindowForOrder,
  type ShippingSettings,
} from '@/lib/shipping-schedule'
import { hasCdekSenderHandoff, stateFromCdekStatus } from '@/lib/customer-state'
import { CdekShipmentDirection } from '@/lib/cdek-domain'
import { reverseTrackingTarget } from '@/lib/order-returns'
import { YMarketOrderStatus } from '@/lib/ymarket-domain'
import { messageStateFor } from '@/lib/message-state'

const PAGE_SIZE = 50
const ORDERS_REFRESH_INTERVAL_MS = 10 * 60_000
const MISSION_CONTROL_KEY = 'active-delivery'

function saveOrderFields(orderId: number, fields: OrderFieldsDraft): Promise<void> {
  return updateOrderState(orderId, { fields })
}

function dispatchDefaultsForOrder(order: YMarketOrder, settings: ShippingSettings) {
  const origin = settings.origins[0] || DEFAULT_SHIPPING_SETTINGS.origins[0]
  const window = nextDispatchWindowForOrder(origin, order.creationDate)
  return window
    ? { shipDate: window.localDate, shipTime: window.time }
    : { shipDate: getDefaultShipDate(), shipTime: origin.dispatchTimes[0] || '10:30' }
}

function statusFiltersFromSearch(search: string, fallback: readonly string[]): Set<string> {
  const requested = new Set(new URLSearchParams(search).get('status')?.split(',').filter((value) => (
    value === YMarketOrderStatus.Delivery
    || value === YMarketOrderStatus.Processing
    || value === YMarketOrderStatus.Delivered
    || value === YMarketOrderStatus.Pickup
    || value === YMarketOrderStatus.Unpaid
    || value === YMarketOrderStatus.Cancelled
  )) ?? [])
  return requested.size > 0 ? requested : new Set(fallback)
}

export default function Home() {
  const { search } = useLocation()
  const orders = useOrdersStore((s) => s.orders)
  const buyerMap = useOrdersStore((s) => s.buyerMap)
  const nextPageToken = useOrdersStore((s) => s.nextPageToken)
  const missionSummary = useOrdersStore((s) => s.missionSummary)
  const missionLoading = useOrdersStore((s) => s.missionLoading)
  const missionError = useOrdersStore((s) => s.missionError)

  const [, setLoading] = useState(false)
  const [, setLoadingMore] = useState(false)
  const [, setError] = useState('')
  const [selected, setSelected] = useState<YMarketOrder | null>(null)
  const [navigationRevision, setNavigationRevision] = useState(0)
  const [copied, setCopied] = useState(false)
  const [copiedFirst, setCopiedFirst] = useState(false)
  const [copiedCourier, setCopiedCourier] = useState(false)
  const [activeStatuses, setActiveStatuses] = useState<Set<string>>(
    () => statusFiltersFromSearch(search, ['DELIVERY', 'PROCESSING', 'UNPAID']),
  )
  const [manualTrack, setManualTrack] = useState('')
  const [manualShipTime, setManualShipTime] = useState('10:30')
  const [manualShipDate, setManualShipDate] = useState('')
  const [cdekEstimate, setCdekEstimate] = useState<string | null>(null)
  const [cdekTracking, setCdekTracking] = useState<CdekTrackingData | null>(null)
  const [returnCdekTracking, setReturnCdekTracking] = useState<CdekTrackingData | null>(null)
  const [returnCdekTrackingLoading, setReturnCdekTrackingLoading] = useState(false)
  const [messageStates, setMessageStates] = useState<MessageDispatch[]>([])
  const [markingManual, setMarkingManual] = useState<MsgType | null>(null)
  const [sendingType, setSendingType] = useState<MsgType | null>(null)
  const [sendError, setSendError] = useState('')
  const [manualMarkError, setManualMarkError] = useState('')
  const [greetingNameIdx, setGreetingNameIdx] = useState<number | null>(null)
  const [chatHistory, setChatHistory] = useState<OrderChatHistory | null>(null)
  const [chatLoading, setChatLoading] = useState(false)
  const [chatError, setChatError] = useState('')
  const [chatOpen, setChatOpen] = useState(false)
  const [pendingChatDelivery, setPendingChatDelivery] = useState<PendingChatDelivery | null>(null)
  const [chatSendFlight, setChatSendFlight] = useState<ChatSendFlight | null>(null)
  const [shippingSettings, setShippingSettings] = useState<ShippingSettings>(DEFAULT_SHIPPING_SETTINGS)
  const directOrderAttempt = useRef<number | null>(null)
  const consumedAppendRequests = useRef(new Set<string>())
  const fetchedStatuses = useMemo(() => {
    if (activeStatuses.size === 0) return activeStatuses
    // The separate monthly-return browser and return filter need cancelled
    // orders. Mission Control totals come from its complete PostgreSQL route.
    return new Set([...activeStatuses, YMarketOrderStatus.Cancelled])
  }, [activeStatuses])
  const activeFetchKey = useRef(statusKey(fetchedStatuses))

  // Re-render every 30s to keep relative timestamps fresh
  const [, setTick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30_000)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    let cancelled = false
    apiFetch('/api/settings/shipping-origin')
      .then(async (response) => {
        const body = await response.json()
        if (response.ok && body.settings && !cancelled) setShippingSettings(body.settings)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    const onPopState = () => {
      if (!new URL(window.location.href).searchParams.get('orderId')) setSelected(null)
      setNavigationRevision((revision) => revision + 1)
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  const fetchOrders = useCallback(
    async (append = false, pageToken = '', force = false) => {
      const key = statusKey(fetchedStatuses)
      const appendRequestKey = append ? `${key}:${pageToken}` : ''
      if (
        appendRequestKey
        && consumedAppendRequests.current.has(appendRequestKey)
      ) return
      const {
        lastFetchedAt,
        fetchedForStatuses,
        beginOrdersFetch,
        acceptOrdersFetch,
        updateBuyerMap,
      } =
        useOrdersStore.getState()
      if (!force && !append && lastFetchedAt !== null && fetchedForStatuses === key) return
      const request = beginOrdersFetch(key)

      if (appendRequestKey) consumedAppendRequests.current.add(appendRequestKey)
      if (append) setLoadingMore(true)
      else setLoading(true)
      setError('')
      try {
        const data = await apiFetchOrders({
          limit: String(PAGE_SIZE),
          status: fetchedStatuses.size > 0 ? [...fetchedStatuses].join(',') : undefined,
          pageToken: pageToken || undefined,
          fresh: force,
        })
        if (activeFetchKey.current !== key) return
        const newOrders = data.orders || []
        const token = data.paging?.nextPageToken || null
        const accepted = acceptOrdersFetch(key, request, newOrders, token, append)
        if (!accepted.applied) return
        // A stale next page makes the whole list stale; a fresh one cannot
        // clear a stale first page it was appended to.
        if (!append) {
          consumedAppendRequests.current.clear()
        }
        const buyers: Record<number, BuyerInfo> = {}
        for (const o of newOrders) {
          if (o.buyer) buyers[o.orderId] = o.buyer
        }
        if (Object.keys(buyers).length > 0) updateBuyerMap(buyers)
      } catch (e: unknown) {
        if (useOrdersStore.getState().ordersRequest === request) {
          if (appendRequestKey) consumedAppendRequests.current.delete(appendRequestKey)
          setError(e instanceof Error ? e.message : 'Ошибка загрузки')
        }
      } finally {
        if (useOrdersStore.getState().ordersRequest === request) {
          setLoading(false)
          setLoadingMore(false)
        }
      }
    },
    [fetchedStatuses]
  )

  useEffect(() => {
    const key = statusKey(fetchedStatuses)
    activeFetchKey.current = key
    useOrdersStore.getState().hydrateCache(key)
    const requestedOrderId = Number(new URL(window.location.href).searchParams.get('orderId'))
    if (!Number.isSafeInteger(requestedOrderId)) setSelected(null)
    fetchOrders(false, '', true)
  }, [fetchedStatuses, fetchOrders])

  useEffect(() => {
    const id = setInterval(() => {
      useOrdersStore.getState().invalidate()
      fetchOrders(false, '')
    }, ORDERS_REFRESH_INTERVAL_MS)
    return () => clearInterval(id)
  }, [fetchOrders])

  const refreshMissionControl = useCallback(async () => {
    const store = useOrdersStore.getState()
    const request = store.beginMissionFetch(MISSION_CONTROL_KEY)
    try {
      const summary = await apiFetchMissionControl()
      useOrdersStore.getState().acceptMissionFetch(MISSION_CONTROL_KEY, request, summary)
    } catch (cause: unknown) {
      useOrdersStore.getState().failMissionFetch(
        MISSION_CONTROL_KEY,
        request,
        cause instanceof Error ? cause.message : 'Полная сводка доставки временно недоступна.',
      )
    }
  }, [])

  useEffect(() => {
    refreshMissionControl()
    const id = setInterval(refreshMissionControl, ORDERS_REFRESH_INTERVAL_MS)
    return () => clearInterval(id)
  }, [refreshMissionControl])

  useEffect(() => {
    setActiveStatuses(statusFiltersFromSearch(search, ['DELIVERY', 'PROCESSING', 'UNPAID']))
  }, [search])

  function selectOrder(order: YMarketOrder, updateHistory = true) {
    if (updateHistory && window.matchMedia('(max-width: 767px)').matches) {
      const url = new URL(window.location.href)
      if (url.searchParams.get('orderId') !== String(order.orderId)) {
        url.searchParams.set('orderId', String(order.orderId))
        window.history.pushState({ orderId: order.orderId }, '', url)
      }
    }
    setSelected(order)
    setCdekEstimate(null)
    setCdekTracking(null)
    setReturnCdekTracking(null)
    setReturnCdekTrackingLoading(false)
    setMessageStates(order.notificationState?.messageStates || [])
    setSendError('')
    setManualMarkError('')
    setChatHistory(null)
    setChatError('')
    setChatOpen(false)
    setPendingChatDelivery(null)
    setChatSendFlight(null)

    const saved = loadOrderFields(order.orderId)
    const apiTrack = order.delivery?.tracks?.[0]?.trackCode || ''
    const suggestedDispatch = dispatchDefaultsForOrder(order, shippingSettings)

    if (saved) {
      setManualShipDate(saved.shipDate ?? suggestedDispatch.shipDate)
      setManualShipTime(saved.shipTime ?? suggestedDispatch.shipTime)
      setManualTrack(apiTrack || saved.track || '')
      setGreetingNameIdx(saved.greetingPartIndex !== undefined ? parseInt(saved.greetingPartIndex, 10) : null)
    } else {
      setManualShipDate(suggestedDispatch.shipDate)
      setManualShipTime(suggestedDispatch.shipTime)
      setManualTrack('')
      setGreetingNameIdx(null)
    }

    if (order.buyer) {
      useOrdersStore.getState().updateBuyerMap({ [order.orderId]: order.buyer })
    } else if (order.campaignId && !buyerMap[order.orderId]) {
      fetchBuyer(order.campaignId, order.orderId)
        .then((data) => {
          if (data.buyer)
            useOrdersStore.getState().updateBuyerMap({ [order.orderId]: data.buyer })
        })
        .catch(() => {})
    }

    getOrderState(order.orderId)
      .then((data) => {
        if (!data.kv) return
        const state = data.state || {}
        const fields = data.fields || null

        setMessageStates(state.messageStates || [])

        if (state.chatId) saveChatId(order.orderId, state.chatId)

        if (fields) {
          if (fields.shipDate) setManualShipDate(fields.shipDate)
          if (fields.shipTime) setManualShipTime(fields.shipTime)
          if (fields.track) setManualTrack(fields.track)
        }
      })
      .catch(() => {})
  }

  const toggleManualMark = useCallback(async (kind: MsgType) => {
    if (!selected) return
    const current = messageStateFor({ messageStates }, kind)
    if (current && current.origin !== 'operator_manual_mark') return
    setMarkingManual(kind)
    setManualMarkError('')
    try {
      if (current) {
        await unmarkManualMessage(selected.orderId, kind)
        setMessageStates((previous) => previous.filter((state) => state !== current))
      } else {
        const mark = await markManualMessage(selected.orderId, kind)
        const legacyKind = kind === 'firstMsg' ? 'first_message' : kind === 'confirmMsg' ? 'confirmation' : 'courier'
        setMessageStates((previous) => [...previous, {
          kind: legacyKind, dispatchedAt: mark.markedAt, origin: 'operator_manual_mark', chatId: null,
        }])
      }
    } catch (cause) {
      setManualMarkError(cause instanceof Error ? cause.message : 'Не удалось изменить отметку. Повторите через минуту.')
    } finally {
      setMarkingManual(null)
    }
  }, [messageStates, selected])

  const orderFields = useMemo(
    () => ({ shipDate: manualShipDate, shipTime: manualShipTime, track: manualTrack }),
    [manualShipDate, manualShipTime, manualTrack],
  )
  const { markEdited: markOrderFieldsEdited, saveError: orderFieldsSaveError } = useOrderFieldsAutosave({
    orderId: selected?.orderId ?? null,
    fields: orderFields,
    save: saveOrderFields,
  })

  useEffect(() => {
    if (!selected) return
    const city = getCity(selected)
    if (!city) return
    let cancelled = false
    fetchCdekEstimate(city)
      .then((data) => {
        if (cancelled || !data.tariffs.length) return
        const cheapest = data.tariffs.reduce(
          (min, t) => (t.delivery_sum < min.delivery_sum ? t : min),
          data.tariffs[0]
        )
        setCdekEstimate(`${cheapest.period_min}–${cheapest.period_max} дн.`)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [selected])

  useEffect(() => {
    if (!selected) return
    const trackCode = selected.delivery?.tracks?.[0]?.trackCode || manualTrack
    if (!trackCode) return
    let cancelled = false
    fetchCdekTracking(trackCode, selected.orderId)
      .then((data) => {
        if (cancelled) return
        setCdekTracking(data)
        if (data._cache?.hit) {
          fetchCdekTracking(trackCode, selected.orderId, true)
            .then((fresh) => { if (!cancelled) setCdekTracking(fresh) })
            .catch(() => {})
        }
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [selected, manualTrack])

  useEffect(() => {
    if (!selected) return
    const target = reverseTrackingTarget(selected)
    if (!target) return
    let cancelled = false
    setReturnCdekTrackingLoading(true)
    fetchCdekTracking(target.cdekNumber, target.orderId)
      .then((data) => {
        if (!cancelled) setReturnCdekTracking(data)
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setReturnCdekTrackingLoading(false)
      })
    return () => { cancelled = true }
  }, [selected])

  const refreshChat = useCallback(async (silent = false): Promise<OrderChatHistory | null> => {
    if (!selected) return null
    if (!silent) setChatLoading(true)
    try {
      const data = await fetchOrderChatHistory(selected.orderId, loadChatId(selected.orderId))
      setChatHistory(data)
      setChatError('')
      if (data.chatId) saveChatId(selected.orderId, data.chatId)
      return data
    } catch (error) {
      if (!silent) setChatError(error instanceof Error ? error.message : 'Не удалось загрузить чат')
      return null
    } finally {
      if (!silent) setChatLoading(false)
    }
  }, [selected])

  useEffect(() => {
    if (!selected) return
    refreshChat()
  }, [selected, refreshChat])

  useEffect(() => {
    if (!selected || pendingChatDelivery?.status !== 'checking' || !pendingChatDelivery.sentAt) return
    let cancelled = false
    const orderId = selected.orderId
    const pollDelaysMs = [1_000, 2_000, 4_000, 8_000, 15_000]
    const verify = async () => {
      for (const [index, delayMs] of pollDelaysMs.entries()) {
        await new Promise(resolve => setTimeout(resolve, delayMs))
        if (cancelled || selected.orderId !== orderId) return
        setPendingChatDelivery(current => current?.status === 'checking' ? { ...current, attempt: index + 1 } : current)
        if (pendingChatDelivery.dryRun) {
          // Two one-second cycles make the local simulation behave like an
          // eventually-consistent provider without touching Yandex.
          if (index < 1) continue
          const simulatedMessage = {
            messageId: pendingChatDelivery.afterMessageId! + 1,
            createdAt: pendingChatDelivery.sentAt!,
            sender: 'PARTNER' as const,
            message: pendingChatDelivery.text,
          }
          setChatHistory(current => ({
            chatId: current?.chatId ?? null,
            status: current?.status,
            confirmationCode: current?.confirmationCode ?? null,
            messages: [...(current?.messages || []), simulatedMessage],
          }))
          setPendingChatDelivery(current => current ? { ...current, status: 'confirmed', attempt: index + 1 } : current)
          return
        }
        const history = await refreshChat(true)
        if (history && chatContainsSentMessage(history, pendingChatDelivery.text, pendingChatDelivery.sentAt!, pendingChatDelivery.afterMessageId)) {
          setPendingChatDelivery(current => current ? { ...current, status: 'confirmed', attempt: index + 1 } : current)
          return
        }
      }
      if (!cancelled) setPendingChatDelivery(current => current?.status === 'checking' ? { ...current, status: 'delayed' } : current)
    }
    verify()
    return () => { cancelled = true }
  }, [selected, pendingChatDelivery?.status, pendingChatDelivery?.sentAt, pendingChatDelivery?.text, refreshChat])

  const refreshChatAndVerify = useCallback(async () => {
    const history = await refreshChat()
    if (history) {
      setPendingChatDelivery(current => current && historyConfirmsPendingChatDelivery(history, current)
        ? { ...current, status: 'confirmed' }
        : current)
    }
  }, [refreshChat])

  async function sendMessage(type: MsgType, text: string, origin?: ChatSendOrigin) {
    if (!selected || sendingType) return
    if (type === 'confirmMsg' && !handoffAccepted) {
      setSendError('Подтверждение передачи доступно только после принятия посылки СДЭК.')
      return
    }
    if (messageStateFor({ messageStates }, type) && !confirm('Уже отправлено. Отправить ещё раз?')) return
    if (origin) setChatSendFlight({ ...origin, id: Date.now() })
    else setChatOpen(true)
    const afterMessageId = Math.max(0, ...(chatHistory?.messages.map(message => message.messageId) || []))
    setPendingChatDelivery({ text, status: 'sending', attempt: 0, afterMessageId })
    setSendingType(type)
    setSendError('')
    try {
      const cachedChatId = loadChatId(selected.orderId)
      const data = await apiSendChatMessage({
        orderId: selected.orderId,
        message: text,
        chatId: cachedChatId,
        type,
      })
      if (!data.dryRun) {
        saveChatId(selected.orderId, data.chatId)
        const entry: SentEntry = { sentAt: data.sentAt, chatId: data.chatId }
        saveSent(selected.orderId, type, entry)
        const legacyKind = type === 'firstMsg' ? 'first_message' : type === 'confirmMsg' ? 'confirmation' : 'courier'
        setMessageStates((previous) => [
          ...previous.filter((state) => state.kind !== legacyKind),
          { kind: legacyKind, dispatchedAt: data.sentAt, origin: 'operator_send', chatId: data.chatId },
        ])
      }
      setPendingChatDelivery({ text, sentAt: data.sentAt, status: 'checking', attempt: 0, afterMessageId, dryRun: data.dryRun })
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Ошибка отправки'
      setSendError(message)
      setPendingChatDelivery({ text, status: 'failed', attempt: 0, error: message, afterMessageId })
    } finally {
      setSendingType(null)
    }
  }

  const buyerName = selected
    ? formatOrderCustomerName(selected, buyerMap[selected.orderId] || null)
    : EM_DASH
  const nameParts = selected
    ? orderGreetingNameParts(selected, buyerMap[selected.orderId] || null)
    : []
  const firstMsgName = resolveGreetingName(nameParts, greetingNameIdx)
  const firstMsgDate = manualShipDate
    ? new Date(manualShipDate).toLocaleDateString('ru-RU', {
        day: '2-digit', month: '2-digit', year: 'numeric',
      })
    : ''

  const activeTemplateText = useMemo(() => getActiveTemplateText(), [])
  const activePrenotifText = useMemo(() => getActivePrenotifTemplateText(), [])
  const activeCourierText = useMemo(() => getActiveCourierTemplateText(), [])

  const transitPlan = getCdekTransitPlan(cdekTracking)
  const currentCdekStatus = latestCdekStatus(cdekTracking?.statuses)?.code
  const selectedShipment = selected ? orderShipment(selected) : undefined
  const projectedHandoffAccepted = selectedShipment
    ? (Boolean(selectedShipment.handoffAccepted) || hasCdekSenderHandoff({
        direction: selectedShipment.direction,
        shipmentState: selectedShipment.state,
        currentStatusCode: selectedShipment.statusCode,
      }))
    : false
  const trackingMatchesProjection = !selectedShipment?.cdekNumber
    || !cdekTracking?.cdek_number
    || selectedShipment.cdekNumber === cdekTracking.cdek_number
  const trackingHandoffAccepted = hasCdekSenderHandoff({
    direction: cdekTracking?.is_return || cdekTracking?.is_reverse
      ? CdekShipmentDirection.Return
      : (trackingMatchesProjection && selectedShipment?.direction)
        || CdekShipmentDirection.Outbound,
    shipmentState: stateFromCdekStatus(currentCdekStatus),
    currentStatusCode: currentCdekStatus,
    statusHistory: cdekTracking?.statuses,
  })
  const handoffAccepted = projectedHandoffAccepted
    || (trackingMatchesProjection && trackingHandoffAccepted)
  const templateData = selected
    ? orderToTemplate(selected, buyerName, {
        track: manualTrack,
        transitDays: transitPlan ? String(transitPlan.days) : '',
        deliveryDate: transitPlan?.plannedDeliveryDate || '',
        shipTime: manualShipTime,
        region: '',
        shipDate: manualShipDate,
        firstName: firstMsgName,
      })
    : null

  const templateText = useMemo(
    () => (handoffAccepted && templateData
      ? renderHandoffTemplateWithText(activeTemplateText, templateData, transitPlan)
      : null),
    [activeTemplateText, handoffAccepted, templateData, transitPlan]
  )

  const firstMessageText =
    firstMsgName && firstMsgDate
      ? renderPrenotifTemplate(activePrenotifText, firstMsgName, firstMsgDate)
      : null
  const courierMessageText = composeCourierMessage(activeCourierText, cdekTracking, {
    firstName: firstMsgName,
    cdekNumber: cdekTracking?.cdek_number || selected?.delivery?.tracks?.[0]?.trackCode || manualTrack || 'не указан',
    confirmationCode: chatHistory?.confirmationCode || undefined,
  })

  const missingFields: string[] = []
  if (selected) {
    if (!buyerName || buyerName === EM_DASH) missingFields.push('Покупатель')
    if (!manualShipDate) missingFields.push('Дата отправки')
    if (!manualShipTime) missingFields.push('Время доставки')
    if (!(selected.delivery?.tracks?.[0]?.trackCode || manualTrack)) missingFields.push('Трек-номер')
  }
  const isReady = missingFields.length === 0

  const firstMsgMissing: string[] = []
  if (selected) {
    if (!firstMsgName) firstMsgMissing.push('Имя')
    if (!firstMsgDate) firstMsgMissing.push('Дата отправки')
  }

  function handleCopy() {
    if (!templateText || !isReady) return
    navigator.clipboard.writeText(templateText).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  function handleCopyFirst() {
    if (!firstMessageText) return
    navigator.clipboard.writeText(firstMessageText).then(() => {
      setCopiedFirst(true)
      setTimeout(() => setCopiedFirst(false), 2000)
    })
  }

  function handleCopyCourier() {
    if (!courierMessageText) return
    navigator.clipboard.writeText(courierMessageText).then(() => {
      setCopiedCourier(true)
      setTimeout(() => setCopiedCourier(false), 2000)
    })
  }

  useEffect(() => {
    const rawOrderId = new URL(window.location.href).searchParams.get('orderId')
    const orderId = rawOrderId ? Number(rawOrderId) : Number.NaN
    if (!Number.isSafeInteger(orderId) || selected?.orderId === orderId) return
    const target = orders.find((order) => order.orderId === orderId)
    if (target) {
      selectOrder(target, false)
      return
    }
    if (!Number.isSafeInteger(orderId) || directOrderAttempt.current === orderId) return
    directOrderAttempt.current = orderId
    void fetchOrderById(orderId)
      .then((order) => {
        if (!order) throw new Error('Заказ не найден')
        if (Number(new URL(window.location.href).searchParams.get('orderId')) !== orderId) return
        selectOrder(order, false)
      })
      .catch((cause) => {
        setError(cause instanceof Error ? cause.message : 'Не удалось открыть заказ')
      })
  // selectOrder intentionally reads the latest component state when the list changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigationRevision, orders, selected?.orderId])

  const closeMissionOrder = useCallback(() => {
    const url = new URL(window.location.href)
    if (window.history.state?.missionDrawer && url.searchParams.has('orderId')) {
      window.history.back()
      return
    }
    url.searchParams.delete('orderId')
    window.history.replaceState({}, '', url)
    setSelected(null)
  }, [])

  function openMissionOrder(order: YMarketOrder) {
    const url = new URL(window.location.href)
    url.searchParams.set('orderId', String(order.orderId))
    window.history.pushState({ missionDrawer: true, orderId: order.orderId }, '', url)
    selectOrder(order, false)
  }

  const drawerError = orderFieldsSaveError || manualMarkError
  const orderDetails = selected ? (
    <>
    {drawerError && (
      <div role="alert" className="mb-5 rounded-md border border-st-unpaid/30 bg-st-unpaid/8 px-4 py-3 text-[13px] text-ink-2">
        {drawerError}
      </div>
    )}
    <OrderDetailContent
      order={selected}
      messageStates={messageStates}
      dbsProps={{
        buyerName,
        nameParts,
        greetingNameIdx,
        manualShipDate,
        manualShipTime,
        manualTrack,
        cdekEstimate,
        cdekTracking,
        returnCdekTracking,
        returnCdekTrackingLoading,
        messageStates,
        copied,
        copiedFirst,
        copiedCourier,
        sendingType,
        markingManual,
        sendError,
        isReady,
        handoffAccepted,
        missingFields,
        templateText,
        firstMessageText,
        firstMsgMissing,
        courierMessageText,
        chatHistory,
        chatLoading,
        chatError,
        chatOpen,
        pendingChatDelivery,
        chatSendFlight,
        onChatOpenChange: setChatOpen,
        onChatSendFlightComplete: () => {
          setChatSendFlight(null)
          setChatOpen(true)
        },
        onRefreshChat: refreshChatAndVerify,
        onShipDateChange: (value) => {
          setManualShipDate(value)
          markOrderFieldsEdited()
          saveOrderField(selected.orderId, 'shipDate', value)
        },
        onShipTimeChange: (value) => {
          setManualShipTime(value)
          markOrderFieldsEdited()
          saveOrderField(selected.orderId, 'shipTime', value)
        },
        onTrackChange: (value) => {
          setManualTrack(value)
          markOrderFieldsEdited()
          saveOrderField(selected.orderId, 'track', value)
        },
        onGreetingPartChange: (index) => {
          setGreetingNameIdx(index)
          saveOrderField(selected.orderId, 'greetingPartIndex', String(index))
        },
        onCopy: handleCopy,
        onCopyFirst: handleCopyFirst,
        onCopyCourier: handleCopyCourier,
        onSendFirst: (origin) => firstMessageText && sendMessage('firstMsg', firstMessageText, origin),
        onSendConfirm: (origin) => isReady && templateText && sendMessage('confirmMsg', templateText, origin),
        onSendCourier: (origin) => courierMessageText && sendMessage('courierMsg', courierMessageText, origin),
        onToggleManualMark: toggleManualMark,
      }}
    />
    </>
  ) : null

  return (
      <div className="relative h-full overflow-hidden bg-ivory">
        <main
          className={`h-full overscroll-contain ${selected ? 'overflow-hidden' : 'overflow-y-auto'}`}
          aria-hidden={selected ? true : undefined}
        >
          <MissionControl
            summary={missionSummary}
            orders={orders}
            buyerMap={buyerMap}
            loading={missionLoading}
            ordersComplete={nextPageToken === null}
            error={missionError}
            lastFetchedAt={missionSummary ? Date.parse(missionSummary.generatedAt) : null}
            onRefresh={refreshMissionControl}
            onSelect={openMissionOrder}
            shippingOrigin={shippingSettings.origins[0]}
          />
        </main>
        {selected && (
          <OrderDetailDrawer orderId={selected.orderId} onClose={closeMissionOrder}>
            {orderDetails}
          </OrderDetailDrawer>
        )}
      </div>
    )
}
